import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChange, getUserData } from './services/userService';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import Register from './pages/Register';
import JobSeekerDashboard from './pages/JobSeekerDashboard';
import EmployerDashboard from './pages/EmployerDashboard';
import CompanyProfileManagement from './pages/CompanyProfileManagement';
import CompaniesList from './pages/CompaniesList';
import CompanyDetail from './pages/CompanyDetail';
import PostJob from './pages/PostJob';
import ManageJobs from './pages/ManageJobs';
import Jobs from './pages/Jobs';
import JobDetail from './pages/JobDetail';
import SavedJobs from './pages/SavedJobs';
import PublicHome from './pages/PublicHome';
import PublicJobsPage from './pages/PublicJobsPage';
import SeekerProfile from './pages/SeekerProfile';
import ApplicationHistory from './pages/ApplicationHistory';
import JobApplications from './pages/JobApplications';
import EmployerApplications from './pages/EmployerApplications';
import StorageTest from './components/StorageTest';
import FirestoreTest from './components/FirestoreTest';
import LoadingSpinner from './components/LoadingSpinner';
import ManageUsers from './pages/ManageUsers';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import TeamDashboard from './pages/TeamDashboard';
import TeamUsers from './pages/TeamUsers';
import TeamCompanies from './pages/TeamCompanies';
import TeamJobs from './pages/TeamJobs';
import ProtectedRoute from './routes/ProtectedRoute';
import DashboardRedirect from './components/DashboardRedirect';
import { getDemoAccountUserType } from './utils/demoAccounts';
import SuperAdminPackages from './pages/SuperAdminPackages';
import AgentDashboard from './pages/AgentDashboard';
import SuperAdminJobs from './pages/SuperAdminJobs';
import SuperAdminCompanies from './pages/SuperAdminCompanies';
import SuperAdminAds from './pages/SuperAdminAds';
import PackagesPage from './pages/PackagesPage';
import MySubscription from './pages/MySubscription';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsAndConditions from './pages/TermsAndConditions';
import ShippingPolicy from './pages/ShippingPolicy';
import RefundPolicy from './pages/RefundPolicy';
import DisclaimerPolicy from './pages/DisclaimerPolicy';
import Contact from './pages/Contact';
import About from './pages/About';
import AgentDetailsManagement from './pages/AgentDetailsManagement';
import NotificationSettings from './components/NotificationSettings';
import DeleteAccount from './pages/DeleteAccount';
import LandingPage from './pages/LandingPage';
import SeekerDatabase from './pages/SeekerDatabase';
import AdminUserProfile from './pages/AdminUserProfile';
import BillingHistory from './pages/BillingHistory';
import Marketing from './pages/Marketing';
import NewPackage from './pages/NewPackage';
import SuperAdminMarketingPackage from './pages/SuperAdminMarketingPackage';
import FranchiseLogin from './pages/Franchise/FranchiseLogin';
import FranchiseDashboard from './pages/Franchise/FranchiseDashboard';
import SuperAdminFranchiseManagement from './pages/SuperAdminFranchiseManagement';
import FranchiseUserList from './pages/Franchise/FranchiseUserList';
import FranchiseAddEmployer from './pages/Franchise/FranchiseAddEmployer';
import FranchiseCompanies from './pages/Franchise/FranchiseCompanies';
import FranchiseJobs from './pages/Franchise/FranchiseJobs';
import FranchiseProfile from './pages/Franchise/FranchiseProfile';

import BottomNavigation from './components/BottomNavigation';

function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const effectiveUserType = userData?.userType || getDemoAccountUserType(user?.email);

  // Expose a manual refresh so post-payment pages can force a fresh userData fetch
  const refreshUserData = async (uid) => {
    const targetUid = uid || user?.uid;
    if (!targetUid) return;
    const userDataResult = await getUserData(targetUid);
    if (userDataResult.success) {
      setUserData(userDataResult.data);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      if (user) {
        const userDataResult = await getUserData(user.uid);
        if (userDataResult.success) {
          setUserData(userDataResult.data);
        }
        setUser(user);
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);
  if (loading) {
    return <LoadingSpinner />;
  }
  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} userData={userData} />
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<LandingPage />} />
          <Route
            path="/login"
            element={!user ? <Login /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/register"
            element={!user ? <Register /> : <Navigate to="/dashboard" />}
          />

          {/* Policy Pages */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/shipping" element={<ShippingPolicy />} />
          <Route path="/refund" element={<RefundPolicy />} />
          <Route path="/disclaimer" element={<DisclaimerPolicy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/landing" element={<LandingPage />} />

          {/* Admin User Profile View - For super admin to view/edit any user */}
          <Route
            path="/admin/user/:userId"
            element={
              <ProtectedRoute requiredUserType="superadmin">
                <AdminUserProfile />
              </ProtectedRoute>
            }
          />

          {/* Super Admin Routes */}
          <Route
            path="/superadmin/*"
            element={
              <ProtectedRoute requiredUserType="superadmin">
                <Routes>
                  <Route index element={<Navigate to="dashboard" />} />
                  <Route path="dashboard" element={<SuperAdminDashboard />} />
                  <Route path="users" element={<ManageUsers />} />
                  <Route path="packages" element={<SuperAdminPackages />} />
                  <Route path="marketing-package" element={<SuperAdminMarketingPackage />} />
                  <Route path="companies" element={<SuperAdminCompanies />} />
                  <Route path="jobs" element={<SuperAdminJobs />} />
                  <Route path="subscriptions" element={<SuperAdminDashboard />} />
                  <Route path="analytics" element={<SuperAdminDashboard />} />
                  <Route path="ads" element={<SuperAdminAds />} />
                  <Route path="franchises" element={<SuperAdminFranchiseManagement />} />
                  <Route path="agents/:agentId" element={<AgentDetailsManagement />} />
                </Routes>
              </ProtectedRoute>
            }
          />

          {/* Team Routes */}
          <Route
            path="/team/*"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'team' ? (
                <Routes>
                  <Route path="dashboard" element={<TeamDashboard user={user} userData={userData} />} />
                  <Route path="users" element={<TeamUsers />} />
                  <Route path="companies" element={<TeamCompanies />} />
                  <Route path="jobs" element={<TeamJobs />} />
                </Routes>
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />

          {/* Agent Routes */}
          <Route
            path="/agent/dashboard"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'agent' ? (
                <AgentDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />

          {/* Franchise Routes */}
          <Route
            path="/franchise/login"
            element={!user ? <FranchiseLogin /> : <Navigate to="/dashboard" />}
          />
          <Route
            path="/franchise/dashboard"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'franchise' ? (
                <FranchiseDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/users"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'franchise' ? (
                <FranchiseUserList user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/add-employer"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'franchise' ? (
                <FranchiseAddEmployer user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/companies"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'franchise' ? (
                <FranchiseCompanies user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/jobs"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'franchise' ? (
                <FranchiseJobs user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/franchise/profile"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'franchise' ? (
                <FranchiseProfile user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={<DashboardRedirect user={user} userData={userData} loading={loading} refreshUserData={refreshUserData} />}
          />
          <Route
            path="/dashboard/jobseeker"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'jobseeker' ? (
                <JobSeekerDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/dashboard/employer"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ? (
                <EmployerDashboard user={user} userData={userData} />
              ) : (
                <Navigate to="/dashboard" />
              )
            }
          />
          <Route
            path="/company-profile"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ?
                <CompanyProfileManagement user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/companies"
            element={<CompaniesList />}
          />
          <Route
            path="/companies/:companyId"
            element={<CompanyDetail />}
          />
          <Route
            path="/post-job"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ?
                <PostJob user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/manage-jobs"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ?
                <ManageJobs user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/jobs"
            element={<Jobs user={user} userData={userData} />}
          />
          <Route
            path="/jobs/:jobId"
            element={<JobDetail user={user} userData={userData} />}
          />
          <Route
            path="/saved-jobs"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'jobseeker' ?
                <SavedJobs user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/seeker-profile"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'jobseeker' ?
                <SeekerProfile user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/application-history"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'jobseeker' ?
                <ApplicationHistory user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/notification-settings"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'jobseeker' ?
                <NotificationSettings user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/jobs/:jobId/applications"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ?
                <JobApplications user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/applications"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ?
                <EmployerApplications user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/packages"
            element={
              user ?
                <PackagesPage user={user} userData={userData} refreshUserData={refreshUserData} /> :
                <Navigate to="/login" />
            }
          />
          <Route
            path="/my-subscription"
            element={
              user ?
                <MySubscription user={user} userData={userData} /> :
                <Navigate to="/login" />
            }
          />
          <Route
            path="/database"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              (effectiveUserType === 'employer' || effectiveUserType === 'superadmin') ?
                <SeekerDatabase user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/billing-history"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ?
                <BillingHistory user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />
          <Route
            path="/marketing"
            element={
              !user ? <Navigate to="/login" /> :
              !effectiveUserType ? <LoadingSpinner /> :
              effectiveUserType === 'employer' ?
                <Marketing user={user} userData={userData} /> :
                <Navigate to="/dashboard" />
            }
          />

          <Route
            path="/storage-test"
            element={<StorageTest />}
          />
          <Route
            path="/firestore-test"
            element={<FirestoreTest />}
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>

        {/* Global Bottom Navigation for Job Seekers and Employers */}
        {user && (userData?.userType === 'jobseeker' || userData?.userType === 'employer') && (
          <BottomNavigation userData={userData} />
        )}
      </div>
    </Router>
  );
}

export default App;
